import { useState, useEffect, useRef } from "react";
import { Box, Button, List, ListItemButton, ListItemIcon, ListItemText, Collapse, FormControl, Tooltip, Checkbox, FormControlLabel, IconButton, CircularProgress, Select, InputLabel, MenuItem, TextField, InputAdornment, Tabs, Tab } from "@mui/material";

import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import BlockIcon from '@mui/icons-material/Block';
import SearchIcon from '@mui/icons-material/Search';
import LibraryAddCheckOutlinedIcon from '@mui/icons-material/LibraryAddCheckOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { useTranslation, withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { includes, isEmpty, isEqual, keys } from "lodash";
import { format } from "date-fns";

import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import SegmentedControl from "@plannel/components/ui/SegmentedControl";
import StyledSwitch from "@plannel/components/ui/StyledSwitch";
import AppCard, { CARD_HEADER_SX, SECTION_TITLE_SX } from "@plannel/components/ui/AppCard";
import StatPill from "@plannel/components/ui/StatPill";
import useJobProgress from "@plannel/hooks/useJobProgress";
import BaseConfigModal, { CONFIG_TAB_SX } from "@plannel/components/modal/BaseConfigModal";
import dataLoadValidationService from "@plannel/services/data/data-load-validation-service";
import dataHistoryService from "@plannel/services/data/data-history-service";
import "@plannel/scss/components/DataLoadValidation.scss"
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import ColorUtil from "@plannel/pages/utils/color-util";
import ResponseUtil from "../utils/response-util";
import CodeUtil from "../utils/code-util";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { FS } from "@plannel/utils/font-util";

const MSG_MULTI_PART_MAX_SIZE = "MSG_MultipartMaxSize";
const MSG_SERVER_ERROR = "MSG_ServerError";
const MSG_IMPORT_FAILED = "MSG_ImportFailed";
const DIALOG_TITLE = {
  EXPORT: "export",
  IMPORT: "import",
  DELETE_IMPORT: "deleteAndImport",
  TRUNCATE: "truncate",
  DELETE: "delete",
}
const { snackBarMessageColor } = ColorUtil;
const { deleteInsertTables, deleteTables } = CodeUtil;

/* 신제품 맵핑(z_npi_profile) 은 모두 삭제 시 반영 기준을 골라 부분 삭제할 수 있다 */
const NPI_PROFILE_TABLE_NAME = "npiProfile";
const APPLY_TYPE_ALL = "ALL";
const APPLY_TYPES = ["ACTUAL", "PLAN"];

/* 좌측 목록 적재 여부 필터 — 테이블이 30개를 넘어 "아직 안 올린 것"만 보고 싶은 때가 많다 */
const STATUS_ALL = "ALL";
const STATUS_HAS_DATA = "HAS_DATA";
const STATUS_NO_DATA = "NO_DATA";

/*
 * 여러 데이터를 한 번에 내보낼 때 쓰는 고정 옵션.
 *   목록에서 고르는 흐름에는 옵션을 물을 자리가 없으므로, 백업 용도로 가장 쓸모 있는 조합으로 고정한다.
 *   (EXPORT_OPTION "2" = 헤더 및 데이터, SEPARATOR_OPTION "0" = CSV, BOM 포함 = Excel 에서 바로 열림)
 *   기간 조건은 넣지 않는다 — 기간이 필요한 경우는 테이블을 하나 골라 내보내기 화면에서 지정한다.
 */
const BULK_EXPORT_OPTION = "2";
const BULK_EXPORT_SEPARATOR = "0";

/* 세로 flex 안에 두면 SegmentedControl 의 트랙이 열 폭만큼 늘어난다 — 내용 너비로 붙여 둔다 */
const SEGMENT_LEFT_SX = { alignSelf: "flex-start" };

/*
 * 파일 크기 표기. 무조건 MB 로 나누던 탓에 수 KB 짜리 CSV 가 전부 "0.0 MB" 로 보였다 —
 * 크기를 알 수 없을 뿐 아니라 파일이 비었다는 오해도 준다. 값에 맞는 단위를 고른다.
 */
const formatFileSize = (bytes) => {
  if (bytes == null || Number.isNaN(bytes)) {
    return "";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/* 오류 행 뷰어의 "사유 전체" 필터 값 */
const REASON_ALL = "__ALL__";

/* 서버가 오류 파일에 남기는 행 수 상한 (BulkImportService.MAX_SAVE_ERRORS) — 잘렸는지 화면이 말해준다 */
const MAX_SAVED_ERROR_ROWS = 1000;

/*
 * 오류 사유 문구에서 컬럼명을 뽑는다.
 *   서버 문구가 "[sales_dt] Please check the data format..." 꼴이라 대괄호 안이 문제 컬럼이다.
 *   중복 키처럼 대괄호가 없는 사유도 있어 없으면 빈 값을 준다.
 */
const parseErrorColumn = (errorName) => (errorName ?? "").match(/^\[([^\]]+)\]/)?.[1] ?? "";

/* 컬럼 표시를 뺀 순수 사유 — 같은 사유끼리 묶을 때 쓴다 */
const parseErrorReason = (errorName) => (errorName ?? "").replace(/^\[[^\]]+\]\s*/, "").trim();

/*
 * 사유 칩 라벨. 서버 문구가 길고 "...The column data type is date / number" 처럼 끝에서만 갈리는 것이 많아
 * 뒤를 잘라내면 서로 구분이 안 된다. 그래서 가운데를 줄여 앞뒤를 모두 남긴다.
 */
const shortenReason = (reason, limit = 52) => {
  if (reason.length <= limit) return reason;
  const head = Math.ceil((limit - 1) * 0.55);
  return `${reason.slice(0, head).trimEnd()}…${reason.slice(reason.length - (limit - 1 - head)).trimStart()}`;
};

/* 우측 탭. 값은 redux 뷰 상태(`expanded`)에 그대로 저장한다 — 아코디언 시절 쓰던 자리를 물려받는다 */
const TAB_IMPORT = "import";
const TAB_EXPORT = "export";
const TAB_DELETE = "delete";

const DataLoadValidation = ({ t, viewName }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [expanded, setExpanded] = useState(reduxViewState?.expanded ?? "");
  const [tableInfos, setTableInfos] = useState([]);
  const [tableInfo, setTableInfo] = useState(reduxViewState?.tableInfo ?? {});
  const [workingTable, setWorkingTable] = useState("");
  const [tableInfoDataCount, setTableInfoDataCount] = useState(0);
  const [bulkExporting, setBulkExporting] = useState(false);
  const [tablesLoaded, setTablesLoaded] = useState(false);
  const exportStartDateRef = useRef("");
  const exportEndDateRef = useRef("");
  const deleteStartDateRef = useRef("");
  const deleteEndDateRef = useRef("");
  const deleteApplyTypeRef = useRef(APPLY_TYPE_ALL);
  const exportAllRef = useRef(false);
  const exportOptionRef = useRef("0");

  const onDialogClose = () => {
    setDialogInfo({ open: false, title: "", content: "" });
  }

  const handleDialog = (response, _, el) => {
    if (response && el) {
      el();
    }
  }

  const handleDialogOpen = (action, jobTarget, el) => {
    switch (action) {
      case DIALOG_TITLE.EXPORT: {
        const isDateRangeTable = DATE_RANGE_EXPORT_TABLES.includes(jobTarget) && exportOptionRef.current === "2";
        if (!isDateRangeTable || exportAllRef.current) {
          setDialogInfo({ open: true, title: `${t(action)} [${t(jobTarget)}]`, content: t("MSG_ExportConfirm"), el });
          break;
        }
        const startDateInfo = format(exportStartDateRef.current, "yyyy-MM-dd");
        const endDateInfo = format(exportEndDateRef.current, "yyyy-MM-dd");
        if (exportEndDateRef.current < exportStartDateRef.current) {
          setDialogInfo({ open: true, title: "info", content: t("MSG_SetEndDate") });
          break;
        }
        setDialogInfo({ open: true, title: `${t(action)} [${t(jobTarget)}]`, content: `${t("MSG_ExportConfirm")} \n[${startDateInfo} ~ ${endDateInfo}]`, el });
        break;
      }
      case DIALOG_TITLE.IMPORT: {
        setDialogInfo({ open: true, title: `${t(action)} [${t(jobTarget)}]`, content: `${t("MSG_ImportConfirm")}`, el });
        break;
      }
      case DIALOG_TITLE.DELETE_IMPORT: {
        setDialogInfo({
          open: true, title: `${t(action)} [${t(jobTarget)}]`,
          content: `${t("MSG_DeleteImportConfirm", { column: deleteInsertTables[jobTarget] })} ${t("MSG_ImportConfirm")}`, el
        });
        break;
      }
      case DIALOG_TITLE.TRUNCATE: {
        /* 반영 기준을 골라 부분 삭제하는 경우(신제품 맵핑)에는 어떤 기준이 지워지는지 함께 알려준다 */
        const applyType = deleteApplyTypeRef.current;
        const applyTypeInfo = jobTarget.tableName === NPI_PROFILE_TABLE_NAME && applyType !== APPLY_TYPE_ALL
          ? ` \n[${t("applyType")}: ${t(applyType === "PLAN" ? "planApply" : "actualApply")}]`
          : "";
        setDialogInfo({ open: true, title: `${t(action)} [${t(jobTarget.tableName)}]`, content: `${t("MSG_TruncateConfirm")}${applyTypeInfo}`, el });
        break;
      }
      case DIALOG_TITLE.DELETE: {
        const startDateInfo = format(deleteStartDateRef.current, "yyyy-MM-dd");
        const endDateInfo = format(deleteEndDateRef.current, "yyyy-MM-dd");
        // end date < start date alert
        if (deleteEndDateRef.current < deleteStartDateRef.current) {
          setDialogInfo({ open: true, title: "info", content: t("MSG_SetEndDate") });
          break;
        }
        setDialogInfo({ open: true, title: `${t(action)} [${t(jobTarget)}]`, content: `${t("MSG_DeleteConfirm")} \n[${startDateInfo} ~ ${endDateInfo}]`, el });
        break;
      }
      default: break;
    }
  }

  /* 새 건수가 화면에 들어온 시점을 호출부가 알 수 있도록 프로미스를 돌려준다 */
  const retrieveTables = () =>
    dataLoadValidationService.getTableInfo()
      .then(response => {
        const responseTables = response.data;
        for (const level of responseTables) {
          level.sort((a, b) => {
            return a.step - b.step;
          });
        }
        setTableInfos(responseTables);
      })
      .catch(error => { debug(error); })
      /* 이 조회는 테이블마다 count(id) 를 돌려 수십 초가 걸린다 — 그동안 "데이터 없음" 으로 보이면 안 된다 */
      .finally(() => { setTablesLoaded(true); });

  /*
   * 고른 데이터를 차례로 내보낸다.
   *
   * 서버는 한 요청에 테이블 하나만 처리하므로(`/data/export` 의 TABLE_NAME 이 단수) 순차로 부른다.
   * 응답은 blob 이고 내려받기는 브라우저가 하므로 파일 개수만큼 다운로드가 일어난다 —
   * 브라우저가 "여러 파일 다운로드" 확인을 띄울 수 있어 확인 문구에서 미리 알린다.
   */
  const runBulkExport = async (items) => {
    setBulkExporting(true);

    let failed = 0;
    for (const item of items) {
      const params = new URLSearchParams();
      params.append("JOB_MODULE", item.module);
      params.append("JOB_TABLE", item.table);
      params.append("TABLE_NAME", item.tableName);
      params.append("EXPORT_OPTION", BULK_EXPORT_OPTION);
      params.append("SEPARATOR_OPTION", BULK_EXPORT_SEPARATOR);
      params.append("INCLUDE_BOM", true);

      try {
        const response = await dataLoadValidationService.exportFile(params);
        if (!response.ok) {
          failed += 1;
          continue;
        }

        const disposition = response.headers.get("Content-Disposition");
        const filename = disposition?.split("filename=")[1]?.replaceAll(/["]+/g, "") ?? `${item.tableName}.csv`;
        const blob = await response.blob();

        /* createObjectURL 은 문서가 살아 있는 동안 blob 을 붙잡으므로 클릭 직후 되돌려준다 */
        const objectUrl = window.URL.createObjectURL(blob);
        const aLink = document.createElement("a");
        aLink.href = objectUrl;
        aLink.download = filename;
        aLink.click();
        window.URL.revokeObjectURL(objectUrl);
      } catch (error) {
        debug(error);
        failed += 1;
      }
    }

    setBulkExporting(false);

    /* 성공은 내려받은 파일 자체가 알려주므로 실패했을 때만 말한다 */
    if (failed > 0) {
      setSnackInfo({
        open: true,
        severity: snackBarMessageColor.saveFail,
        content: t("MSG_ExportPartialFailed", { count: failed }),
      });
    }
  }

  const handleExportSelected = (items) => {
    if (isEmpty(items)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoSelectedData") });
      return;
    }

    setDialogInfo({
      open: true,
      title: `${t("export")} (${items.length})`,
      content: t("MSG_ExportSelectedConfirm", { count: items.length }),
      el: () => runBulkExport(items),
    });
  }

  const getTableStatus = (tableInfoParam) => {
    const params = { JOB_TABLE: tableInfoParam.table, TABLE_NAME: tableInfoParam.tableName };

    dataLoadValidationService.getTableStatus(params)
      .then((response) => {
        const data = response.data;
        setTableInfoDataCount(data.count);

        if (data?.enable) {
          if (workingTable === tableInfoParam.tableName) {
            setWorkingTable("");
          }
        } else {
          setWorkingTable(tableInfoParam.tableName);
        }
      }).catch((error) => {
        setWorkingTable(tableInfoParam.tableName);
        debug(error);
      });
  }

  return (
    <Box id={"data-load-validation"} sx={{ mt: 1, mr: 0, display: "inline-flex", height: "calc(100vh - 125px)", width: "100%" }}>
      <LeftSideArea
        setExpanded={setExpanded}
        reduxDispatch={reduxDispatch}
        tableInfos={tableInfos}
        tableInfo={tableInfo}
        viewName={viewName}
        workingTable={workingTable}
        setTableInfo={setTableInfo}
        retrieveTables={retrieveTables}
        onExportSelected={handleExportSelected}
        bulkExporting={bulkExporting}
        tablesLoaded={tablesLoaded}
      />
      {
        Object.keys(tableInfo).length > 0 && (
          <ContentArea
            expanded={expanded}
            getTableStatus={getTableStatus}
            handleDialogOpen={handleDialogOpen}
            reduxDispatch={reduxDispatch}
            retrieveTables={retrieveTables}
            tableInfo={tableInfo}
            tableInfos={tableInfos}
            tableInfoDataCount={tableInfoDataCount}
            setExpanded={setExpanded}
            setSnackInfo={setSnackInfo}
            setTableInfo={setTableInfo}
            setWorkingTable={setWorkingTable}
            snackInfo={snackInfo}
            viewName={viewName}
            workingTable={workingTable}
            exportStartDateRef={exportStartDateRef}
            exportEndDateRef={exportEndDateRef}
            deleteStartDateRef={deleteStartDateRef}
            deleteEndDateRef={deleteEndDateRef}
            deleteApplyTypeRef={deleteApplyTypeRef}
            exportAllRef={exportAllRef}
            exportOptionRef={exportOptionRef}
          />
        )
      }
      <Dialog
        open={dialogInfo.open}
        onClose={onDialogClose}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
        el={dialogInfo.el}
        maxWidth={dialogInfo.maxWidth}
      />
    </Box >
  );
};

const LeftSideArea = (props) => {
  const { t } = useTranslation();
  const { isPermitted } = useMenuAccess();
  const {
    tableInfos,
    tableInfo,
    viewName,
    reduxDispatch,
    workingTable,
    setExpanded,
    setTableInfo,
    retrieveTables,
    onExportSelected,
    bulkExporting,
    tablesLoaded,
  } = props;
  const [levelCollapses, setLevelCollapses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  /* 목록 좁히기 — 30개가 넘는 목록에서 "이름으로 찾기"와 "아직 안 올린 것만" 이 가장 잦은 요구다 */
  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);

  /* 여러 데이터를 골라 한 번에 내보내는 선택 모드. 평소에는 체크박스를 숨겨 목록을 조용히 둔다 */
  const [selectMode, setSelectMode] = useState(false);
  const [selectedTables, setSelectedTables] = useState([]);

  const setLevelOpen = (level, open) => {
    const openDatas = [...levelCollapses];
    openDatas[level] = open;
    setLevelCollapses(openDatas);
  }

  const clickList = (level) => {
    const open = levelCollapses[level];
    setLevelOpen(level, !open);
  }

  const clickListItem = (listItem) => {
    setTableInfo(listItem);
    setExpanded("");
    reduxDispatch(reduxUtil.updateViewState({ viewName, tableInfo: listItem, expanded: "" }));
  }

  const clickValidationItem = () => {
    reduxDispatch(reduxUtil.updateHistoryState("DATA_VALIDATION"));
  }

  /* 검색어는 화면에 보이는 번역명과 원본 키를 모두 본다 — 사용자는 보이는 이름으로 찾는다 */
  const matchesFilter = (item) => {
    const count = Number(item?.dataCount) || 0;
    if (statusFilter === STATUS_HAS_DATA && count === 0) return false;
    if (statusFilter === STATUS_NO_DATA && count > 0) return false;

    const word = keyword.trim().toLowerCase();
    if (!word) return true;

    return `${t(item.tableName)} ${item.tableName}`.toLowerCase().includes(word);
  }

  /* 단계 묶음은 그대로 두고 안쪽만 걸러낸다 — levelCollapses 가 단계 인덱스를 쓰기 때문 */
  const visibleLevels = (tableInfos ?? []).map(list => (list ?? []).filter(matchesFilter));
  const visibleItems = visibleLevels.flat();

  /* 선택은 필터와 무관하게 남긴다 — 검색어를 바꿨다고 고른 것이 사라지면 안 된다 */
  const selectedItems = (tableInfos ?? []).flat().filter(item => selectedTables.includes(item.tableName));
  const allVisibleSelected = visibleItems.length > 0
    && visibleItems.every(item => selectedTables.includes(item.tableName));

  const toggleSelect = (item) => {
    setSelectedTables(prev => (prev.includes(item.tableName)
      ? prev.filter(name => name !== item.tableName)
      : [...prev, item.tableName]));
  }

  const toggleSelectAll = () => {
    setSelectedTables(allVisibleSelected ? [] : visibleItems.map(item => item.tableName));
  }

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedTables([]);
  }

  useEffect(() => {
    setIsLoading(true);
    if (isLoading) {
      retrieveTables();
    }

    return (() => {
      setIsLoading(false);
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    const levelOpens = new Array(tableInfos.length).fill(true);
    setLevelCollapses(levelOpens);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableInfos]);

  const LevelListItem = (listItem) => {
    const { tableName, dataCount, enable, importable } = listItem;
    const count = isNaN(dataCount) ? "0" : Number(dataCount).toLocaleString();

    /*
     * 적재 상태 아이콘. 네 상태가 한 줄에서 구분돼야 하는데 기본 크기(24px) 민짜 체크는
     * 이름보다 무거워 목록 전체가 체크 열처럼 보였다. 18px 로 낮추고 "채움 정도"로 상태를 읽게 한다.
     *   적재됨 = 채운 원 / 비었음 = 빈 원 / 불가 = 흐린 금지 / 진행 중 = 스피너
     * (대시(—)는 "없음"보다 "해당 없음"으로 읽혀 비어 있는 것과 불가를 구분하기 어려웠다.)
     */
    const StateIcon = () => {
      if (!importable || !enable) {
        return <BlockIcon sx={{ fontSize: 18, color: "action.disabled" }} />
      }

      if ((tableInfo.isRun && tableInfo.tableName === tableName) || workingTable === tableName) {
        return <CircularProgress size={16} />
      }

      if (dataCount === 0) {
        return <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: "grey.400" }} />
      }

      return <CheckCircleRoundedIcon sx={{ fontSize: 18, color: "success.main" }} />
    }

    /* 선택 모드에서는 같은 클릭이 "고르기"가 된다 — 화면 전환과 섞이면 실수한다 */
    return (
      <ListItemButton
        className="list-item"
        key={tableName}
        selected={!selectMode && tableInfo?.tableName === tableName}
        onClick={() => (selectMode ? toggleSelect(listItem) : clickListItem(listItem))}
      >
        {selectMode && (
          <Checkbox
            size="small"
            checked={selectedTables.includes(tableName)}
            tabIndex={-1}
            disableRipple
            sx={{ p: 0, mr: 1 }}
          />
        )}
        <ListItemIcon className="list-icon">
          <StateIcon />
        </ListItemIcon>
        <ListItemText primary={t(tableName)} />
        {/*
          건수는 Typography 를 쓰지 않는다 — 전역 SCSS(.list-main .MuiTypography-root)가 크기를 덮어써
          이름과 같은 크기로 튀어 보인다. StatPill 이 같은 이유로 Box 를 쓴다.
        */}
        <Box
          component="span"
          sx={{ ml: 1, fontSize: FS.BODY, color: "text.secondary", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
        >
          {count}
        </Box>
      </ListItemButton>
    )
  }

  const LevelList = (listInfos) => {
    const { listData, tableIndex } = listInfos;
    const primaryName = `${t("step")} ${tableIndex + 1}`;

    /* 이 단계에서 몇 개가 적재됐는지 — 접어 둬도 진행 상황이 보이게 머리글에 남긴다 */
    const rows = listData ?? [];
    const loadedCount = rows.filter(row => Number(row?.dataCount) > 0).length;

    return (
      <div key={tableIndex}>
        {/*
          폴더 아이콘을 뺐다 — 단계는 폴더가 아니고, 아이콘 자리(36px)만 차지한 채
          아래 상태 아이콘 열과 시선이 겹쳤다. 머리글은 구역 라벨답게 작고 조용하게 두고,
          대신 그 자리에 "몇 개 적재됐는지"라는 실제 정보를 넣는다.
        */}
        <ListItemButton
          className="level-list-item"
          onClick={() => clickList(tableIndex)}
          sx={{ py: 0.75, borderTop: tableIndex === 0 ? 0 : 1, borderColor: "divider" }}
        >
          <ListItemText
            primary={primaryName}
            primaryTypographyProps={{
              sx: { fontSize: FS.SUB, fontWeight: 700, letterSpacing: "0.04em", color: "text.secondary" },
            }}
          />
          {rows.length > 0 && (
            <Box
              component="span"
              sx={{
                mr: 0.75, fontSize: FS.CAPTION, fontVariantNumeric: "tabular-nums",
                color: loadedCount === rows.length ? "success.main" : "text.disabled",
                whiteSpace: "nowrap",
              }}
            >
              {loadedCount}/{rows.length}
            </Box>
          )}
          {levelCollapses[tableIndex] ? <ExpandLess sx={{ fontSize: 18, color: "text.disabled" }} /> : <ExpandMore sx={{ fontSize: 18, color: "text.disabled" }} />}
        </ListItemButton>
        <Collapse in={levelCollapses[tableIndex]} timeout="auto" unmountOnExit>
          <List className="list-main" component="div" disablePadding>
            {
              listData ? listData.map((item) => {
                return LevelListItem(item);
              }) : ""
            }
          </List>
        </Collapse>
      </div>
    )
  }

  const ValidationList = () => {
    // 데이터 검증 화면이 권한 밖이면 좌측 목록에서 "검증" 항목 자체를 빼 둔다.
    if (!isPermitted("DATA_VALIDATION")) return null;

    return (
      <div key={"Validation"}>
        <ListItemButton className="level-list" key={"validation"} onClick={clickValidationItem}>
          {/*
            폴더가 아니라 "검증" 이다 — 단계 그룹에서 폴더 아이콘을 걷어내면서 여기만 남으면
            같은 그림이 다른 뜻이 된다. 체크가 찍힌 클립보드로 무엇을 여는 자리인지 그림으로 말한다.
            (다중 선택 토글이 Checklist 를 이미 쓰므로 그것과는 다른 글리프를 쓴다.)
          */}
          <ListItemIcon className="list-icon">
            <FactCheckOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
          </ListItemIcon>
          <ListItemText primary={t("validation")} />
        </ListItemButton >
      </div>
    )
  }

  return (
    <Box className="left-side-area">
      {/* 검색 + 적재 여부 필터 — 목록 위에 붙여 스크롤과 함께 사라지지 않게 둔다 */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 1, pt: 1, pb: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <TextField
            fullWidth
            size="small"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder={t("search")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                </InputAdornment>
              ),
              sx: { height: 32, fontSize: FS.TITLE },
            }}
          />
          {/*
            이 모드에서 할 수 있는 일은 "고른 데이터 내보내기" 하나뿐이라 이름도 그렇게 붙인다.
            "다중 선택" 은 무엇을 위한 선택인지 말해 주지 않았다.
            아이콘도 Checklist 에서 바꿨다 — 우측 워크플로우 패널이 같은 글리프를 쓰고 있어
            같은 그림이 다른 뜻이 되고 있었다(components/WorkflowPanel.js).
          */}
          <Tooltip title={t("selectToExport")}>
            <IconButton
              size="small"
              color={selectMode ? "primary" : "default"}
              onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
            >
              <LibraryAddCheckOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <SegmentedControl
          sx={SEGMENT_LEFT_SX}
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: STATUS_ALL, label: t("all") },
            { value: STATUS_HAS_DATA, label: t("hasData") },
            { value: STATUS_NO_DATA, label: t("noData") },
          ]}
        />
      </Box>

      {selectMode && (
        <Box
          sx={{
            display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.75,
            bgcolor: "action.hover", borderTop: 1, borderBottom: 1, borderColor: "divider",
          }}
        >
          <Checkbox
            size="small"
            sx={{ p: 0.5 }}
            checked={allVisibleSelected}
            indeterminate={selectedItems.length > 0 && !allVisibleSelected}
            onChange={toggleSelectAll}
          />
          <Box component="span" sx={{ flexGrow: 1, fontSize: FS.BODY, color: "text.secondary" }}>
            {t("MSG_SelectedCount", { count: selectedItems.length })}
          </Box>
          <Button
            size="small"
            variant="contained"
            startIcon={<FileDownloadOutlinedIcon />}
            disabled={selectedItems.length === 0 || bulkExporting || workingTable !== ""}
            onClick={() => onExportSelected(selectedItems)}
            sx={{ textTransform: "none", fontSize: FS.TITLE, py: 0.25, minWidth: 0 }}
          >
            {t("export")}
            {bulkExporting && <CircularProgress size={14} sx={{ ml: 0.75 }} />}
          </Button>
          <Tooltip title={t("close")}>
            <IconButton size="small" onClick={exitSelectMode}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <List component="nav">
        {
          visibleLevels.map((list, tableIndex) => (
            list.length > 0 ? LevelList({ listData: list, tableIndex }) : null
          ))
        }
        {
          !tablesLoaded && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          )
        }
        {
          tablesLoaded && visibleItems.length === 0 && (
            <Box sx={{ px: 2, py: 3, textAlign: "center", fontSize: FS.SUB, color: "text.secondary" }}>
              {t("MSG_NoData")}
            </Box>
          )
        }
      </List>

      {/*
        검증은 목록 **밖**에 고정한다.
          "데이터를 다 올린 다음 검증" 이라는 순서 때문에 맨 아래에 두었는데, 테이블이 40개를 넘으면서
          끝까지 스크롤해야만 보이는 자리가 됐다 — 순서는 맞지만 눈에 닿지 않으면 없는 것과 같다.
          nav 는 flex-grow:1 + overflow:auto 이므로, 그 형제로 두면 자리는 그대로 맨 아래면서
          스크롤과 무관하게 늘 보인다. (위로 올리면 늘 보이지만 "마지막에 하는 일" 이라는 뜻이 깨진다.)
      */}
      <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: "divider" }}>
        {ValidationList()}
      </Box>
    </Box>
  )
}

const INIT_FILE_STATE = {
  selectedFiles: [],
  currentFile: 'undefined',
  isError: false,
}

const CURRENT_DATE = new ZDate();
const DEFAULT_START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1);
const YEAR = "year";
const MONTH = "month";
const DAY = "day";
const START = "start";
const END = "end";
const DATE_RANGE_EXPORT_TABLES = [
  "salesTransaction", "financePlan", "salesPlan", "salesOrder",
  "inventoryTransaction", "intransitInventory", "shipmentTransaction",
  "exchangeRate", "unitPrice", "purchaseBudget", "materialReceiptPlan", "materialDemand",
  "bfFeatureDate", "bfFeatureSales", "prodOrder", "menuPurchaseOrder", "wip", "menuResourceCapacity"
];
const MULTI_DATE_COLUMN_TABLES = {
  shipmentTransaction: ["eta", "etd", "ata", "atd"],
  intransitInventory:  ["baseDt", "eta", "etd", "atd"],
  materialReceiptPlan: ["baseDt", "eta", "etd", "ata", "atd"],
  materialDemand: ["baseDt", "dueDate"],
  menuPurchaseOrder: ["dueDate", "eta", "etd", "ata", "atd"],
  wip: ["prodDate", "expDate"]
};
const ContentArea = (props) => {
  const { t } = useTranslation();
  const {
    expanded,
    getTableStatus,
    handleDialogOpen,
    reduxDispatch,
    retrieveTables,
    tableInfo,
    tableInfos,
    tableInfoDataCount,
    setExpanded,
    setSnackInfo,
    setTableInfo,
    setWorkingTable,
    snackInfo,
    viewName,
    workingTable,
    exportStartDateRef,
    exportEndDateRef,
    deleteStartDateRef,
    deleteEndDateRef,
    deleteApplyTypeRef,
    exportAllRef,
    exportOptionRef
  } = props;

  const [exportOption, setExportOption] = useState("0");
  const [exportSeparatorOption, setExportSeparatorOption] = useState("0");
  const [includeBOM, setIncludeBOM] = useState(true);
  const [importOption, setImportOption] = useState("0");
  const [importSeparatorOption, setImportSeparatorOption] = useState("0");
  const fileInputRef = useRef();
  const [fileUploadState, setFileUploadState] = useState(INIT_FILE_STATE);
  const [isImportable, setIsImportable] = useState(true);
  const [dragOver, setDragOver] = useState(false);

  /*
   * 방금 끝난 가져오기의 결과. 임포트 API 는 {success, message} 만 돌려주므로
   * 성공/오류 건수와 오류 행은 적재 이력에서 받아온다 (이미 서버가 오류 CSV 를 남기고 있다).
   */
  const [importResult, setImportResult] = useState(null);
  const [errorViewerOpen, setErrorViewerOpen] = useState(false);
  const [errorReason, setErrorReason] = useState(REASON_ALL);

  /* 아코디언 시절의 `expanded` 를 탭 값으로 물려 쓴다 — 빈 값(테이블을 새로 고른 직후)이면 가져오기 */
  const activeTab = [TAB_IMPORT, TAB_EXPORT, TAB_DELETE].includes(expanded) ? expanded : TAB_IMPORT;

  /* 이 테이블에 파일을 올릴 수 없는 상태(선행 미충족·업로드 대상 아님)와 작업 중 상태 */
  const importBlocked = !(isImportable || tableInfo.tableName === workingTable);

  /*
   * 진행 여부의 진실은 서버에 있다 — 탭을 옮기면 이 화면이 통째로 언마운트돼
   * workingTable(로컬 state) 이 사라진다. 지금 보고 있는 테이블이 서버에서 돌고 있으면
   * 돌아왔을 때도 진행 중으로 보이고, 끝나면 목록과 결과를 다시 읽는다.
   */
  const { running: serverRunning, probe: probeJobs } = useJobProgress(
    tableInfo?.tableName ? [tableInfo.tableName] : [],
    /* 훅이 기다릴 수 있게 프로미스를 돌려준다 — 새 건수가 들어온 뒤에 "끝남" 으로 바뀐다 */
    () => retrieveTables()
  );

  const running = workingTable !== "" || serverRunning;

  /*
   * 왜 막혔는지.
   *
   * 서버는 enable 을 boolean 하나로만 준다(ImportSchemaService.preCheckLevelCondition).
   * 그 판정은 "낮은 레벨의 필수 테이블이 모두 비어 있지 않고, 그중 도는 작업이 없을 것" 인데,
   * level·essential·dataCount 가 목록에 이미 있으므로 앞 조건은 화면에서 그대로 다시 계산할 수 있다.
   * 앞 조건이 다 맞는데도 막혀 있으면 남은 이유는 "선행 테이블이 작업 중" 하나뿐이다.
   *
   * ※ 서버는 두 테이블 사이의 역할(role) 교차 검사도 하므로, 이 목록이 실제보다 넓게 나올 수 있다.
   */
  const unmetPrerequisites = (tableInfos ?? [])
    .flat()
    .filter(item => item.essential && item.level < tableInfo.level && Number(item.dataCount) === 0);

  const importBlockedByPrereq = importBlocked && tableInfo.importable && unmetPrerequisites.length > 0;
  const importBlockedByRunning = importBlocked && tableInfo.importable && unmetPrerequisites.length === 0;

  /* 서버가 한 요청에 파일 하나만 받으므로 첨부도 항상 하나다 — 드롭존이 이 값으로 상태를 바꾼다 */
  const selectedFile = Array.from(fileUploadState?.selectedFiles ?? [])[0];
  const hasSelectedFile = Boolean(selectedFile);

  const goToTable = (item) => {
    setTableInfo(item);
    handleTabChange(TAB_IMPORT);
  };

  /* 오류 행을 사유로 묶는다 — 42행이 실은 서너 가지 문제인 경우가 대부분이다 */
  const errorRows = importResult?.errHistory ?? [];
  const errorReasons = errorRows.reduce((acc, row) => {
    const reason = parseErrorReason(row.errorName) || t("error");
    acc[reason] = (acc[reason] ?? 0) + 1;
    return acc;
  }, {});
  const filteredErrorRows = errorReason === REASON_ALL
    ? errorRows
    : errorRows.filter(row => (parseErrorReason(row.errorName) || t("error")) === errorReason);

  /* 원본 행을 헤더에 맞춰 열로 되돌린다. 열 수가 안 맞으면 되돌리지 않고 원문 그대로 보여준다 */
  const errorSplitter = importResult?.delimiterOptn === 1 ? "\t" : ",";
  const errorHeaders = importResult?.headers ?? [];
  const splitErrorData = (errorData) => {
    const cells = (errorData ?? "").split(errorSplitter);
    return errorHeaders.length > 1 && cells.length === errorHeaders.length ? cells : null;
  };

  const downloadErrorRows = () => {
    const header = [t("reason"), t("data")].join(",");
    const body = errorRows
      .map(row => [row.errorName, row.errorData].map(cell => `"${(cell ?? "").replaceAll('"', '""')}"`).join(","))
      .join("\n");

    /* BOM 을 붙여야 Excel 이 한글을 깨지 않고 연다 (내보내기의 supportBOM 과 같은 이유) */
    const blob = new Blob([`﻿${header}\n${body}`], { type: "text/csv;charset=utf-8" });
    const objectUrl = window.URL.createObjectURL(blob);
    const aLink = document.createElement("a");
    aLink.href = objectUrl;
    aLink.download = `${tableInfo.tableName}_error.csv`;
    aLink.click();
    window.URL.revokeObjectURL(objectUrl);
  };

  const [exportStartDate, setExportStartDate] = useState(DEFAULT_START_DATE);
  const [exportEndDate, setExportEndDate] = useState(CURRENT_DATE);
  const [exportAll, setExportAll] = useState(false);
  const [exportDateColumn, setExportDateColumn] = useState("");
  const [deleteStartDate, setDeleteStartDate] = useState(DEFAULT_START_DATE);
  const [deleteEndDate, setDeleteEndDate] = useState(CURRENT_DATE);
  const [deleteDateColumn, setDeleteDateColumn] = useState("");
  const [deleteAll, setDeleteAll] = useState(false);
  const [deleteApplyType, setDeleteApplyType] = useState(APPLY_TYPE_ALL);
  const isNpiProfileTable = tableInfo?.tableName === NPI_PROFILE_TABLE_NAME;

  const getExportParameter = () => {

    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append("JOB_MODULE", tableInfo.module);
    urlSearchParams.append("JOB_TABLE", tableInfo?.table);
    urlSearchParams.append("TABLE_NAME", tableInfo?.tableName);
    urlSearchParams.append("EXPORT_OPTION", exportOption);
    urlSearchParams.append("SEPARATOR_OPTION", exportSeparatorOption);
    urlSearchParams.append("INCLUDE_BOM", includeBOM);

    if (DATE_RANGE_EXPORT_TABLES.includes(tableInfo?.tableName) && exportOption === "2" && !exportAll) {
      urlSearchParams.append("START_DATE", format(exportStartDateRef.current, "yyyy-MM-dd"));
      urlSearchParams.append("END_DATE", format(exportEndDateRef.current, "yyyy-MM-dd"));
      if (exportDateColumn) {
        urlSearchParams.append("DATE_COLUMN", exportDateColumn);
      }
    }

    return urlSearchParams;
  }

  const exportFile = () => {
    setTableInfo({ ...tableInfo, isRun: true });
    setWorkingTable(tableInfo.tableName);
    dataLoadValidationService.exportFile(getExportParameter())
      .then((response) => {
        if (response.ok) {
          const filename = response.headers.get('Content-Disposition').split('filename=')[1].replaceAll(/["]+/g, "");

          /* TODO: File Export 시 언어팩을 적용하고자 한다면 주석 해제 후 사용 
           * TODO: FileStorageService.java [resourceFileName = resourceFileName.replaceAll("menu[_-]?", "");] 주석 해제 후 사용
           */
          // const disposition = response.headers.get('Content-Disposition');
          // const rawFilename = disposition?.split('filename=')[1]?.replaceAll(/["]+/g, "") ?? "";
          // const suffix = rawFilename.replace(/^[^.(]+/, "");
          // const filename = (rawFilename && suffix) ? t(tableInfo.tableName) + suffix : rawFilename;

          response.blob().then(blob => {
            const aLink = document.createElement('a');
            aLink.href = window.URL.createObjectURL(blob);
            aLink.download = filename;
            aLink.click();
          });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
        }
      })
      .catch((e) => {
        debug(e);
      })
      .finally(() => {
        setTableInfo({ ...tableInfo, isRun: false });
        setWorkingTable("");
      });
  }

  const getImportFormData = () => {
    const formData = new FormData();
    formData.append("JOB_MODULE", tableInfo.module);
    formData.append("JOB_TABLE", tableInfo?.table);
    formData.append("TABLE_NAME", tableInfo?.tableName);
    formData.append("IMPORT_OPTION", importOption);
    formData.append("SEPARATOR_OPTION", importSeparatorOption);
    formData.append("JOB_LEVEL", tableInfo?.level);
    formData.append("JOB_STEP", tableInfo?.step);

    Array.from(fileUploadState.selectedFiles).forEach(file => {
      formData.append("FILES", file);
    });

    return formData;
  }

  /*
   * 올린 파일의 헤더 줄을 미리 읽어 둔다.
   *
   * 오류 이력은 {errorName, errorData} 두 열뿐이고 errorData 는 원본 행이 통째로 한 문자열이다.
   * 서버가 헤더를 같이 주지 않으므로, 보내기 직전에 브라우저에서 첫 줄을 읽어 두면
   * 결과 화면에서 그 행을 원래 컬럼으로 되돌려 보여줄 수 있다.
   */
  const readHeaderLine = async (file) => {
    try {
      const splitter = importSeparatorOption === "0" ? "," : "\t";
      const head = await file.slice(0, 64 * 1024).text();
      const firstLine = (head.split(/\r?\n/)[0] ?? "").replace(/^﻿/, "");

      return firstLine.split(splitter).map(column => column.trim().replace(/^"|"$/g, ""));
    } catch (error) {
      debug(error);
      return [];
    }
  }

  /*
   * 방금 끝난 작업의 성공/오류 건수와 오류 행을 이력에서 받아온다.
   * 배너를 실제로 띄웠는지 **불리언으로 돌려준다** — 호출부가 "배너가 없을 때만 스낵바"를
   * 판단하는 데 쓴다(아래 importFile 주석 참고).
   */
  const retrieveImportResult = (headers, fileName) =>
    dataHistoryService.getAll({ jobTable: tableInfo.tableName, page: 0, pageSize: 5 })
      .then(response => {
        /* jobTable 은 부분 일치라 이름이 겹치는 테이블이 섞여 들어온다 — 정확히 같은 것만 고른다 */
        const job = (response.data?.results ?? []).find(row => row.jobTableName === tableInfo.tableName);
        if (!job) return false;

        setImportResult({ ...job, headers, fileName });
        setErrorReason(REASON_ALL);
        return true;
      })
      .catch(error => {
        debug(error);
        return false;
      });

  const importFile = async () => {
    setTableInfo({ ...tableInfo, isRun: true });
    setWorkingTable(tableInfo.tableName);
    setImportResult(null);

    const uploaded = Array.from(fileUploadState.selectedFiles)[0];
    const headers = uploaded ? await readHeaderLine(uploaded) : [];

    /*
     * 스낵바는 여기서 바로 띄우지 않고 메시지만 담아 둔다.
     *
     * 적재 이력이 남았으면 위쪽 결과 배너가 같은 서버 메시지에 성공/오류 건수와 "오류 행 보기"까지
     * 얹어 보여준다 — 우측 하단에서 같은 말을 한 번 더 하면 배너를 읽던 시선만 끌어내린다.
     * 성공이든 실패든 마찬가지다(실패 예: "Fail - Import Job Result (Success : 0 / Fail : 88)").
     *
     * 반대로 용량 초과·네트워크 오류처럼 **작업 자체가 시작되지 않으면 이력이 없어 배너가 안 뜬다**.
     * 그때는 스낵바가 유일한 피드백이라 반드시 띄워야 한다.
     * 그래서 기준은 "성공/실패"가 아니라 **"배너가 떴나"** 이고, 그 판정은 retrieveImportResult 가 준다.
     */
    let failureMessage = null;

    probeJobs();

    dataLoadValidationService.importFile(getImportFormData())
      .then((response) => {
        if (response.data?.success) {
          setFileUploadState(INIT_FILE_STATE);
          fileInputRef.current.value = "";
        } else {
          failureMessage = response.data.message ? response.data.message : `${t(MSG_IMPORT_FAILED)} ${t(MSG_SERVER_ERROR)}`;
          setFileUploadState({
            ...fileUploadState,
            currentFile: 'undefined',
            isError: true
          });
        }
      }).catch((error) => {
        const { response } = error;
        let errMsg = `${t(MSG_IMPORT_FAILED)} ${t(MSG_SERVER_ERROR)}`;

        if (isEmpty(response) || response.status === ResponseUtil.httpStatusCode.PayloadTooLarge) {
          errMsg = isEmpty(response) ? t(MSG_MULTI_PART_MAX_SIZE) : t(response?.data);
        }

        failureMessage = errMsg;
        setFileUploadState({
          ...fileUploadState,
          currentFile: 'undefined',
          selectedFiles: [],
          isError: true
        });

        debug(error);
      }).finally(() => {
        retrieveTables();
        retrieveImportResult(headers, uploaded?.name ?? "").then(bannerShown => {
          if (!bannerShown && failureMessage) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: failureMessage });
          }
        });
        setTableInfo({ ...tableInfo, isRun: false });
        setWorkingTable("");
      });
  }

  const truncateTable = () => {
    setTableInfo({ ...tableInfo, isRun: true });
    setWorkingTable(tableInfo.tableName);

    /* 반영 기준을 고르면 해당 기준의 데이터만 삭제한다 (전체 선택 시 기존과 동일하게 모두 삭제) */
    const params = { TABLE_NAME: tableInfo?.tableName };
    if (isNpiProfileTable && deleteApplyType !== APPLY_TYPE_ALL) {
      params.APPLY_TYPE = deleteApplyType;
    }

    dataLoadValidationService.truncate(params)
      .then((response) => {
        const { data } = response;

        if (data?.success) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        }
      })
      .catch((e) => {
        if (isEqual(e.response?.status, ResponseUtil.httpStatusCode.Unauthorized)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.unauthorized, content: t(e.response?.data) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
        }
        debug(e);
      })
      .finally(() => {
        retrieveTables();
        setTableInfo({ ...tableInfo, isRun: false });
        setWorkingTable("");
      });
  }

  const deleteData = () => {
    setTableInfo({ ...tableInfo, isRun: true });
    setWorkingTable(tableInfo.tableName);

    const params = { TABLE_NAME: tableInfo.tableName, START_DATE: deleteStartDate, END_DATE: deleteEndDate,
      ...(deleteDateColumn && { DATE_COLUMN: deleteDateColumn }) };
    dataLoadValidationService.deleteTableData(params)
      .then((response) => {
        const { data } = response;

        const success = data?.success;
        const message = data?.message ? data.message : t("MSG_DeleteFailed");
        if (success) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(message) });
        }
      })
      .catch((e) => {
        if (isEqual(e.response?.status, ResponseUtil.httpStatusCode.Unauthorized)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.unauthorized, content: t(e.response?.data) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
        }
        debug(e);
      })
      .finally(() => {
        retrieveTables();
        setTableInfo({ ...tableInfo, isRun: false });
        setWorkingTable("");
      });
  }

  const selectFile = (event) => {
    setFileUploadState({
      ...fileUploadState,
      selectedFiles: event.target.files,
    });
  }

  const removeFileUploadListItem = (index) => {
    const fileUploadList = [...fileUploadState.selectedFiles];
    fileUploadList.splice(index, 1);
    fileInputRef.current.value = "";

    setFileUploadState({
      ...fileUploadState,
      selectedFiles: fileUploadList,
    });
  }

  const handleTabChange = (value) => {
    reduxDispatch(reduxUtil.updateViewState({ viewName, expanded: value }));
    setExpanded(value);
  };

  const handleDragOver = (event) => {
    /* preventDefault 를 해야 브라우저가 파일을 새 탭으로 열어버리지 않는다 */
    event.preventDefault();
    if (!importBlocked && !running) setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    if (importBlocked || running) return;

    const accept = importSeparatorOption !== "0" ? ".tsv" : ".csv";
    const dropped = Array.from(event.dataTransfer?.files ?? [])
      .filter(file => file.name.toLowerCase().endsWith(accept));

    if (dropped.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_OneFileOnly") });
      return;
    }

    /*
     * 서버가 한 요청에 파일 하나만 받는다 — FileStorageService.uploadMultiFiles 가 첫 파일만 돌려주고
     * 나머지는 저장은 되지만 적재되지 않는다. 조용히 버리지 말고 첫 파일만 받았다고 알린다.
     */
    if (dropped.length > 1) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_OneFileOnly") });
    }

    setFileUploadState({ ...fileUploadState, selectedFiles: [dropped[0]] });
  };

  /*
   * 예전에는 여기 FileUploadListItem 이 있어 드롭존 아래에 "Import File" 목록을 따로 그렸다.
   * 파일 이름·크기·삭제가 드롭존 안으로 들어가면서 같은 파일이 두 번 보이게 되어 없앤다.
   */

  const fileUploadDisabled = (tableInfoParam) => {
    const isDisabled = true;

    if (!tableInfoParam.enable || !tableInfoParam.importable) {

      return !isDisabled;
    }

    return isDisabled;
  }

  const onAcceptHandler = (dateValue, type, option) => {
    if (isEqual(type, START)) {
      dateValue.setHours(0);
      dateValue.setMinutes(0);
      dateValue.setSeconds(0);

      if (option === DIALOG_TITLE.DELETE) {
        setDeleteStartDate(dateValue);
      } else if (option === DIALOG_TITLE.EXPORT) {
        setExportStartDate(dateValue);
      }
    } else {
      dateValue.setHours(23);
      dateValue.setMinutes(59);
      dateValue.setSeconds(59);

      if (option === DIALOG_TITLE.DELETE) {
        setDeleteEndDate(dateValue);
      } else if (option === DIALOG_TITLE.EXPORT) {
        setExportEndDate(dateValue);
      }
    }
  }

  const count = isNaN(tableInfo.dataCount) ? "" : Number(tableInfo.dataCount).toLocaleString();

  /*
   * 함께 지워지는 데이터 목록.
   *
   * 예전에는 "- 이름" 을 줄바꿈으로 이어붙인 한 덩어리 텍스트였다. 하이픈이 글머리 기호 노릇을
   * 하느라 눈에 걸리고, 품목처럼 연관 데이터가 18개인 테이블에서는 카드가 세로로 길게 늘어졌다.
   * 이름 하나하나를 칩으로 두면 기호가 필요 없고, 가로로 흘러 몇 줄에 담긴다.
   */
  /*
   * 이름으로 옮겨지는 것만 남긴다.
   *
   * deleteRelTableList 에는 tableToMenuName 에 없는 이름이 섞여 들어온다(판매 실적이 그렇다).
   * 예전에는 그런 경우 제목만 있고 목록이 빈 경고 상자가 떴다 — 보여줄 게 없으면 상자도 띄우지 않는다.
   */
  const deleteRelNames = (tableInfo?.deleteRelTableList ?? [])
    .map((data) => CodeUtil.tableToMenuName[data])
    .filter(Boolean);

  const makeMessage = (names) => {
    if (names.length === 0) return null;

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
        {names.map((name) => (
          <Box
            key={name}
            component="span"
            sx={{
              display: "inline-flex", alignItems: "center", height: 24, px: 1, borderRadius: 999,
              bgcolor: "background.paper", border: 1, borderColor: "warning.light",
              fontSize: FS.SUB, color: "text.primary", whiteSpace: "nowrap",
            }}
          >
            {t(name)}
          </Box>
        ))}
      </Box>
    );
  };

  useEffect(() => {
    if (Object.keys(tableInfo).length > 0 && tableInfo?.isRun !== true) {
      getTableStatus(tableInfo);
      setIsImportable(fileUploadDisabled(tableInfo));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableInfo.table, tableInfo?.isRun]);

  useEffect(() => {
    setTableInfo({ ...tableInfo, dataCount: tableInfoDataCount });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableInfoDataCount]);

  useEffect(() => {
    exportEndDateRef.current = exportEndDate;
    exportStartDateRef.current = exportStartDate;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportStartDate, exportEndDate]);

  useEffect(() => {
    const firstCol = MULTI_DATE_COLUMN_TABLES[tableInfo.tableName]?.[0] ?? "";
    setExportDateColumn(firstCol);
    setDeleteDateColumn(firstCol);
    setDeleteAll(false);
    setDeleteApplyType(APPLY_TYPE_ALL);
    /* 다른 데이터로 넘어가면 앞선 결과는 그 데이터 이야기가 아니다 */
    setImportResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableInfo.tableName]);

  useEffect(() => {
    exportAllRef.current = exportAll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportAll]);

  useEffect(() => {
    exportOptionRef.current = exportOption;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportOption]);

  useEffect(() => {
    deleteEndDateRef.current = deleteEndDate;
    deleteStartDateRef.current = deleteStartDate;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteStartDate, deleteEndDate]);

  useEffect(() => {
    deleteApplyTypeRef.current = deleteApplyType;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteApplyType]);

  /* 가져오기 옵션은 테이블마다 고를 수 있는 값이 다르다 (삭제 후 추가는 실적성 데이터만 지원) */
  const importOptions = includes(keys(deleteInsertTables), tableInfo.tableName)
    ? [
      { value: "0", label: t("add") },
      { value: "2", label: t("addAndUpdate") },
      { value: "3", label: t("deleteAndAdd") },
    ]
    : [
      { value: "0", label: t("add") },
      { value: "1", label: t("update") },
      { value: "2", label: t("addAndUpdate") },
    ];

  const separatorOptions = [
    { value: "0", label: "CSV" },
    { value: "1", label: "TSV" },
  ];

  const isDateRangeExport = DATE_RANGE_EXPORT_TABLES.includes(tableInfo?.tableName) && exportOption === "2";
  const isPeriodDelete = includes(keys(deleteTables), tableInfo.tableName);

  const dateFieldSlotProps = { textField: { InputProps: { color: "primary", size: "small", sx: { height: 36, width: 168 } } } };

  return (
    <Box sx={{ width: "inherit", height: "100%", overflow: "auto", display: "flex", flexDirection: "column", gap: 1.5 }}>

      {/* 데이터 상태 — 아코디언이 아니라 화면 머리글이다. 어느 데이터를 보고 있는지가 늘 보여야 한다 */}
      <AppCard sx={{ flexShrink: 0, px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            {/* 물리 테이블명(z_xxx)은 보여주지 않는다 — 화면에서 고를 때 쓰는 이름이 아니다 */}
            <Box component="span" sx={SECTION_TITLE_SX}>{t(tableInfo.tableName)}</Box>
            {tableInfo.level != null && (
              <StatPill label={t("step")} value={tableInfo.level} />
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            <StatPill accent label={t("totalData")} value={count} />
            <StatPill label={t("importWorkState")} value={running ? t("running") : t("idle")} />
          </Box>
        </Box>

        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          {running && <CircularProgress size={20} />}
        </Box>
      </AppCard>

      {/* 가져오기 / 내보내기 / 삭제 — 아코디언 3개를 탭으로 바꿔 옵션을 한 화면에서 비교한다 */}
      <AppCard sx={{ flexShrink: 0, display: "flex", flexDirection: "column" }}>
        <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: "divider", px: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <Tabs value={activeTab} onChange={(_, value) => handleTabChange(value)} sx={CONFIG_TAB_SX}>
            <Tab label={t("import")} value={TAB_IMPORT} />
            <Tab label={t("export")} value={TAB_EXPORT} />
            <Tab label={t("delete")} value={TAB_DELETE} />
          </Tabs>

          {/*
            파일 형식(CSV/TSV)은 각 패널 안, 다른 옵션 옆에 둔다.
            예전에는 탭 줄 오른쪽 끝(ml:auto)에 한 자리만 두고 지금 탭에 맞는 값을 넣었는데,
            넓은 화면에서는 탭과 1,800px 씩 떨어져 눈에 안 들어왔다. 이름표도 없어 무엇에 걸린
            옵션인지 알기 어려웠고("Import Option" 은 왼쪽에 이름표를 달고 있어 더 어긋나 보였다),
            컨트롤 하나가 탭에 따라 import/export 값을 번갈아 편집하는 숨은 모드이기도 했다.
            두 값은 원래 따로 기억하므로 패널마다 두는 편이 오히려 정직하다.
          */}
        </Box>

        <Box sx={{ p: 2 }}>

          {/* ── 가져오기 ── */}
          {activeTab === TAB_IMPORT && importBlocked && (
            /*
             * 막혔으면 비활성 드롭존을 보여주는 대신 왜 막혔는지 말한다.
             * 예전에는 버튼만 조용히 비활성이라 사용자가 이유를 알 길이 없었다.
             */
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 720 }}>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                <Box sx={{
                  width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  bgcolor: importBlockedByRunning ? "rgba(4,139,168,0.1)" : "#fdf0e0",
                }}>
                  {importBlockedByRunning
                    ? <CircularProgress size={17} />
                    : <LockOutlinedIcon sx={{ fontSize: 18, color: "warning.dark" }} />}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Box component="span" sx={SECTION_TITLE_SX}>
                    {importBlockedByRunning ? t("MSG_ImportBlockedRunningTitle") : t("MSG_ImportBlockedTitle")}
                  </Box>
                  <Box component="span" sx={{ fontSize: FS.SUB, color: "text.secondary", lineHeight: 1.6 }}>
                    {!tableInfo.importable && t("MSG_ImportNotSupported")}
                    {importBlockedByPrereq && t("MSG_ImportBlockedPrereq", { step: tableInfo.level })}
                    {importBlockedByRunning && t("MSG_ImportBlockedRunning")}
                  </Box>
                </Box>
              </Box>

              {importBlockedByPrereq && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box component="span" sx={CARD_HEADER_SX}>{t("prerequisite")}</Box>
                    <StatPill label={t("noData")} value={unmetPrerequisites.length} />
                  </Box>
                  <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, overflow: "hidden" }}>
                    {unmetPrerequisites.map((item, index) => (
                      <Box
                        key={item.tableName}
                        onClick={() => goToTable(item)}
                        sx={{
                          height: 40, px: 1.5, display: "flex", alignItems: "center", gap: 1, cursor: "pointer",
                          borderTop: index === 0 ? 0 : 1, borderColor: "divider",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <WarningAmberOutlinedIcon sx={{ fontSize: 17, color: "warning.main" }} />
                        <Box component="span" sx={{ flexGrow: 1, fontSize: FS.H2, fontWeight: 500 }}>{t(item.tableName)}</Box>
                        <Box component="span" sx={{ fontSize: FS.BODY, color: "text.secondary" }}>
                          {t("step")} {item.level}
                        </Box>
                        <ChevronRightIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                      </Box>
                    ))}
                  </Box>
                  <Box component="span" sx={{ fontSize: FS.CAPTION, color: "text.secondary", lineHeight: 1.6 }}>
                    {t("MSG_PrerequisiteNote")}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {activeTab === TAB_IMPORT && !importBlocked && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* 방금 끝난 가져오기 결과 — 부분 반영이 흔하므로 결말을 분명히 말한다 */}
              {importResult && (
                <Box
                  sx={{
                    flexShrink: 0, borderRadius: 1.5, p: 1.5, border: 1,
                    borderColor: importResult.errorCnt > 0 ? "warning.light" : "success.light",
                    bgcolor: importResult.errorCnt > 0 ? "#fffbf5" : "#f6faf6",
                    display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
                  }}
                >
                  {importResult.errorCnt > 0
                    ? <WarningAmberOutlinedIcon sx={{ color: "warning.dark" }} />
                    : <CheckCircleOutlineIcon sx={{ color: "success.main" }} />}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    <Box component="span" sx={{ ...CARD_HEADER_SX, color: importResult.errorCnt > 0 ? "warning.dark" : "success.dark" }}>
                      {importResult.errorCnt > 0
                        ? t("MSG_ImportResultPartial", { count: importResult.errorCnt })
                        : t("MSG_ImportResultDone")}
                    </Box>
                    <Box component="span" sx={{ fontSize: FS.SUB, color: "text.secondary" }}>
                      {importResult.fileName} · {importResult.descTxt}
                    </Box>
                  </Box>

                  <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                    <StatPill label={t("successCnt")} value={Number(importResult.successCnt ?? 0).toLocaleString()} />
                    {importResult.errorCnt > 0 && (
                      <>
                        <StatPill accent label={t("errorCnt")} value={Number(importResult.errorCnt).toLocaleString()} />
                        <Button size="small" variant="outlined" sx={{ textTransform: "none" }}
                          onClick={() => setErrorViewerOpen(true)}>
                          {t("errorRows")}
                        </Button>
                      </>
                    )}
                    <IconButton size="small" onClick={() => setImportResult(null)}>
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {/*
                파일을 끌어다 놓거나 눌러서 고른다 — 두 길이 같은 자리에 있어야 찾기 쉽다.
                파일이 붙으면 **이 자리가 그대로 "붙은 파일" 표시**가 된다. 예전에는 파일을 골라도
                안내 문구("Drop a CSV or TSV file here")가 그대로라, 첨부됐는지는 아래 목록을
                따로 찾아봐야 알 수 있었다 — 넓은 화면에서 그 줄은 눈에 잘 안 들어온다.
              */}
              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                  flexShrink: 0, minHeight: 150, borderRadius: 1.5, px: 2, py: 2.5,
                  border: 1, borderStyle: hasSelectedFile ? "solid" : "dashed",
                  borderColor: dragOver || hasSelectedFile ? "primary.main" : "grey.400",
                  bgcolor: dragOver ? "rgba(4,139,168,0.06)" : hasSelectedFile ? "rgba(4,139,168,0.04)" : "grey.50",
                  opacity: importBlocked ? 0.55 : 1,
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1,
                }}
              >
                {/* input 은 두 상태가 같은 것을 쓴다 — label htmlFor 가 가리키는 대상이 하나여야 한다 */}
                <input
                  ref={fileInputRef}
                  accept={importSeparatorOption !== "0" ? ".tsv" : ".csv"}
                  id="contained-button-file"
                  type="file"
                  style={{ display: "none" }}
                  onChange={selectFile}
                />

                {hasSelectedFile ? (
                  <>
                    <CheckCircleOutlineIcon color="primary" sx={{ fontSize: 34 }} />
                    <Box
                      component="span"
                      sx={{
                        fontSize: FS.H1, fontWeight: 500, maxWidth: "100%",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}
                    >
                      {selectedFile?.name}
                    </Box>
                    <Box component="span" sx={{ fontSize: FS.SUB, color: "text.secondary" }}>
                      {formatFileSize(selectedFile?.size)}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }}>
                      <label htmlFor="contained-button-file">
                        <Button variant="outlined" component="span" disabled={importBlocked}
                          sx={{ textTransform: "none" }}>
                          {t("chooseFile")}
                        </Button>
                      </label>
                      <Button
                        color="error"
                        startIcon={<DeleteIcon />}
                        disabled={importBlocked}
                        onClick={() => removeFileUploadListItem(0)}
                        sx={{ textTransform: "none" }}
                      >
                        {t("delete")}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <CloudUploadOutlinedIcon color="primary" sx={{ fontSize: 34 }} />
                    <Box component="span" sx={{ fontSize: FS.H1, fontWeight: 500 }}>{t("MSG_DropFileHere")}</Box>
                    <Box component="span" sx={{ fontSize: FS.SUB, color: "text.secondary" }}>
                      {t("MSG_DropFileTarget", { name: t(tableInfo.tableName) })}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }}>
                      <label htmlFor="contained-button-file">
                        <Button variant="contained" component="span" disabled={importBlocked}
                          sx={{ textTransform: "none" }}>
                          {t("chooseFile")}
                        </Button>
                      </label>
                    </Box>
                    <Box component="span" sx={{ fontSize: FS.CAPTION, color: "text.disabled" }}>
                      {importSeparatorOption !== "0" ? ".tsv" : ".csv"} · {t("MSG_OneFileOnly")}
                    </Box>
                  </>
                )}
              </Box>

              {/* 옵션은 한 줄에 하나씩 세로로 쌓는다 — 이름표와 컨트롤이 늘 같은 왼쪽 선에서 시작한다 */}
              <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Box component="span" sx={CARD_HEADER_SX}>{t("importOption")}</Box>
                <SegmentedControl sx={SEGMENT_LEFT_SX} value={importOption} onChange={setImportOption} options={importOptions} />
              </Box>

              <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Box component="span" sx={CARD_HEADER_SX}>{t("fileFormat")}</Box>
                <SegmentedControl
                  sx={SEGMENT_LEFT_SX}
                  value={importSeparatorOption}
                  onChange={setImportSeparatorOption}
                  options={separatorOptions}
                />
              </Box>

              {/*
                실행 버튼은 옵션들과 같은 왼쪽 줄에 둔다 — ml:auto 로 오른쪽 끝에 보내면
                넓은 화면에서 내용과 멀찍이 떨어져 따로 논다.
                주의 문구는 버튼 위 — 누르기 전에 읽는 순서가 된다.
              */}
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
                <Box component="span" sx={{ fontSize: FS.SUB, color: "text.secondary" }}>
                  {t("MSG_ImportCommitNote")}
                </Box>
                <Button
                  sx={{ textTransform: "none" }}
                  variant="contained"
                  startIcon={<FileUploadOutlinedIcon />}
                  disabled={fileUploadState.selectedFiles.length <= 0 || running}
                  onClick={() => handleDialogOpen(isEqual(importOption, "3") ? DIALOG_TITLE.DELETE_IMPORT
                    : DIALOG_TITLE.IMPORT, tableInfo.tableName, importFile)}
                >
                  {t("uploadAndImport")}
                  {running && <CircularProgress sx={{ ml: 1 }} size={"18px"} />}
                </Button>
              </Box>
            </Box>
          )}

          {/* ── 내보내기 ── */}
          {activeTab === TAB_EXPORT && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* 무엇을 내보낼지 — 헤더만 / 샘플 포함 / 데이터 포함 */}
              <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Box component="span" sx={CARD_HEADER_SX}>{t("exportOption")}</Box>
                <SegmentedControl
                  sx={SEGMENT_LEFT_SX}
                  value={exportOption}
                  onChange={setExportOption}
                  options={[
                    { value: "0", label: t("header") },
                    { value: "1", label: t("headerAndSample") },
                    { value: "2", label: t("headerAndData") },
                  ]}
                />
              </Box>

              {/*
                어떤 파일로 내보낼지. Excel 지원(BOM)은 "무엇을 내보낼지"가 아니라
                파일 인코딩에 붙는 값이라 형식과 한 묶음으로 둔다 — 내보내기 옵션 옆에 두면
                두 그룹 사이에 낀 것처럼 보이고, 이름표(Export Option)와도 맞지 않는다.
              */}
              <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Box component="span" sx={CARD_HEADER_SX}>{t("fileFormat")}</Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                  <SegmentedControl
                    value={exportSeparatorOption}
                    onChange={setExportSeparatorOption}
                    options={separatorOptions}
                  />
                  <Tooltip title={t("MSG_ByteOrderMark")} placement="right-start">
                    <FormControlLabel
                      sx={{ mr: 0 }}
                      control={<StyledSwitch size="small" checked={includeBOM} onChange={() => { setIncludeBOM(!includeBOM) }} sx={{ mr: 2 }} />}
                      label={t("supportBOM")}
                    />
                  </Tooltip>
                </Box>
              </Box>

              {isDateRangeExport && (
                <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Box component="span" sx={CARD_HEADER_SX}>{t("periodSearchCondition")}</Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                    {MULTI_DATE_COLUMN_TABLES[tableInfo?.tableName] && (
                      <FormControl size="small" sx={{ minWidth: 150 }} disabled={exportAll}>
                        <InputLabel>{t("periodSearchCondition")}</InputLabel>
                        <Select
                          value={exportDateColumn}
                          label={t("periodSearchCondition")}
                          onChange={(e) => setExportDateColumn(e.target.value)}
                        >
                          {MULTI_DATE_COLUMN_TABLES[tableInfo.tableName].map(col => (
                            <MenuItem key={col} value={col}>{t(col)}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label={t("periodStart")}
                        views={[YEAR, MONTH, DAY]}
                        format={"yyyy-MM-dd"}
                        openTo={DAY}
                        value={exportStartDate}
                        disabled={exportAll}
                        reduceAnimations
                        onAccept={(e) => { if (e) onAcceptHandler(e, START, DIALOG_TITLE.EXPORT) }}
                        slotProps={dateFieldSlotProps}
                      />
                      <DatePicker
                        label={t("periodEnd")}
                        views={[YEAR, MONTH, DAY]}
                        format={"yyyy-MM-dd"}
                        openTo={DAY}
                        value={exportEndDate}
                        disabled={exportAll}
                        reduceAnimations
                        onAccept={(e) => { if (e) onAcceptHandler(e, END, DIALOG_TITLE.EXPORT) }}
                        slotProps={dateFieldSlotProps}
                      />
                    </LocalizationProvider>
                    {/*
                      기간 전체를 끄는 스위치라 기간 입력칸과 한 줄에 두지만, gap 1(8px)만으로는
                      Period(End) 달력 아이콘에 붙어 같은 컨트롤처럼 보인다.
                      위 File Format ↔ Excel Support 와 같은 간격(24px)으로 떼어 둔다.
                    */}
                    <FormControlLabel
                      sx={{ ml: 2, mr: 0 }}
                      control={<StyledSwitch size="small" checked={exportAll} onChange={() => setExportAll(!exportAll)} sx={{ mr: 2 }} />}
                      label={t("exportAll")}
                    />
                  </Box>
                </Box>
              )}

              {/*
                실행 버튼은 왼쪽 정렬 — 옵션들이 전부 왼쪽에 쌓이는데 버튼만 ml:auto 로 오른쪽 끝에
                보내면 넓은 화면에서 내용과 1,500px 씩 떨어져 따로 노는 것처럼 보인다.
              */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Button
                  sx={{ textTransform: "none" }}
                  variant="contained"
                  startIcon={<FileDownloadOutlinedIcon />}
                  disabled={running}
                  onClick={() => handleDialogOpen(DIALOG_TITLE.EXPORT, tableInfo.tableName, exportFile)}
                >
                  {t("export")}
                  {running && <CircularProgress sx={{ ml: 1 }} size={"18px"} />}
                </Button>
              </Box>
            </Box>
          )}

          {/* ── 삭제 ── */}
          {activeTab === TAB_DELETE && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* 계획반영만 지우는 경우 NPI 이력은 건드리지 않으므로 연관 데이터 안내를 감춘다 */}
              {deleteRelNames.length > 0 && !(isNpiProfileTable && deleteApplyType === "PLAN") && (
                <Box sx={{ flexShrink: 0, border: 1, borderColor: "warning.light", bgcolor: "#fffbf5", borderRadius: 1.5, p: 1.5 }}>
                  <Box component="span" sx={{ ...CARD_HEADER_SX, color: "warning.dark" }}>
                    {t("MSG_DeleteDataAssociated")}
                  </Box>
                  {makeMessage(deleteRelNames)}
                </Box>
              )}

              {isPeriodDelete ? (
                <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Box component="span" sx={CARD_HEADER_SX}>{t("periodSearchCondition")}</Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                    {MULTI_DATE_COLUMN_TABLES[tableInfo?.tableName] && (
                      <FormControl size="small" sx={{ minWidth: 150 }} disabled={deleteAll}>
                        <InputLabel>{t("periodSearchCondition")}</InputLabel>
                        <Select value={deleteDateColumn} label={t("periodSearchCondition")}
                          onChange={(e) => setDeleteDateColumn(e.target.value)}>
                          {MULTI_DATE_COLUMN_TABLES[tableInfo.tableName].map(col => (
                            <MenuItem key={col} value={col}>{t(col)}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label={t("periodStart")}
                        views={[YEAR, MONTH, DAY]}
                        format={"yyyy-MM-dd"}
                        openTo={DAY}
                        value={deleteStartDate}
                        disabled={deleteAll}
                        reduceAnimations
                        onAccept={(e) => { if (e) onAcceptHandler(e, START, DIALOG_TITLE.DELETE) }}
                        slotProps={dateFieldSlotProps}
                      />
                      <DatePicker
                        label={t("periodEnd")}
                        views={[YEAR, MONTH, DAY]}
                        format={"yyyy-MM-dd"}
                        openTo={DAY}
                        value={deleteEndDate}
                        disabled={deleteAll}
                        reduceAnimations
                        onAccept={(e) => { if (e) onAcceptHandler(e, END, DIALOG_TITLE.DELETE) }}
                        slotProps={dateFieldSlotProps}
                      />
                    </LocalizationProvider>
                    {/* Export All 과 같은 간격 — gap 1 만으로는 Period(End) 달력 아이콘에 붙는다 */}
                    <FormControlLabel
                      sx={{ ml: 2, mr: 0 }}
                      control={<StyledSwitch size="small" checked={deleteAll} onChange={() => setDeleteAll(!deleteAll)} sx={{ mr: 2 }} />}
                      label={t("deleteAll")}
                    />
                  </Box>
                </Box>
              ) : isNpiProfileTable && (
                /* 신제품 맵핑은 반영 기준을 골라 그 기준의 데이터만 삭제할 수 있다 */
                <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Box component="span" sx={CARD_HEADER_SX}>{t("applyType")}</Box>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>{t("applyType")}</InputLabel>
                    <Select value={deleteApplyType} label={t("applyType")}
                      onChange={(e) => setDeleteApplyType(e.target.value)}>
                      <MenuItem value={APPLY_TYPE_ALL}>{t("all")}</MenuItem>
                      {APPLY_TYPES.map(type => (
                        <MenuItem key={type} value={type}>{t(type === "PLAN" ? "planApply" : "actualApply")}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* Import 와 같은 규칙 — 경고를 먼저 읽고, 버튼은 왼쪽 아래 */}
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
                <Box component="span" sx={{ fontSize: FS.SUB, color: "error.main" }}>
                  {t("MSG_DeleteIrreversibleNote")}
                </Box>
                <Button
                  sx={{ textTransform: "none" }}
                  color="error"
                  variant="contained"
                  startIcon={<DeleteForeverIcon />}
                  disabled={running || count === "0"}
                  onClick={() => (isPeriodDelete && !deleteAll
                    ? handleDialogOpen(DIALOG_TITLE.DELETE, tableInfo.tableName, deleteData)
                    : handleDialogOpen(DIALOG_TITLE.TRUNCATE, tableInfo, truncateTable))}
                >
                  {isPeriodDelete && !deleteAll ? t("delete") : t("deleteAll")}
                  {running && <CircularProgress sx={{ ml: 1 }} size={"18px"} />}
                </Button>
              </Box>
            </Box>
          )}

        </Box>
      </AppCard>

      {/*
        오류 행 뷰어. 서버가 남긴 오류 CSV 를 이력 API 로 받아 그대로 보여준다 —
        지금까지는 [데이터 적재 이력] 메뉴에서 빨간 숫자를 눌러야만 볼 수 있던 내용이다.
      */}
      <BaseConfigModal
        open={errorViewerOpen}
        onClose={() => setErrorViewerOpen(false)}
        title={t("errorRows")}
        subtitle={`${t(tableInfo.tableName)} · ${importResult?.fileName ?? ""}`}
        cancelLabel={t("close")}
        width={1040}
        paperSx={{ maxWidth: "95vw" }}
        extraActions={
          <Button size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={downloadErrorRows}
            sx={{ textTransform: "none" }}>
            {t("download")}
          </Button>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {/* 사유 필터 — 42행이 서너 가지 문제인 경우가 대부분이라 사유부터 좁힌다 */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {[[REASON_ALL, errorRows.length], ...Object.entries(errorReasons)].map(([reason, reasonCount]) => {
              const selected = errorReason === reason;
              return (
                <Box
                  key={reason}
                  onClick={() => setErrorReason(reason)}
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: 0.75, height: 26, px: 1.25, borderRadius: 999,
                    cursor: "pointer", border: 1, fontSize: FS.SUB, maxWidth: 420,
                    borderColor: selected ? "primary.main" : "divider",
                    bgcolor: selected ? "primary.main" : "background.paper",
                    color: selected ? "primary.contrastText" : "text.primary",
                  }}
                >
                  <Tooltip title={reason === REASON_ALL ? "" : reason}>
                    <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                      {reason === REASON_ALL ? t("all") : shortenReason(reason)}
                    </Box>
                  </Tooltip>
                  <Box component="span" sx={{ fontWeight: 700 }}>{reasonCount}</Box>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "auto", maxHeight: "52vh" }}>
            <Box sx={{ display: "table", width: "100%", borderCollapse: "collapse", fontSize: FS.BODY }}>
              <Box sx={{ display: "table-header-group", bgcolor: "#f0f5fa", position: "sticky", top: 0 }}>
                <Box sx={{ display: "table-row", fontWeight: 700, color: "text.secondary" }}>
                  <Box sx={{ display: "table-cell", p: "6px 8px", width: 52 }}>#</Box>
                  <Box sx={{ display: "table-cell", p: "6px 8px", width: 260 }}>{t("reason")}</Box>
                  {errorHeaders.length > 1
                    ? errorHeaders.map(header => (
                      <Box key={header} sx={{ display: "table-cell", p: "6px 8px", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                        {header}
                      </Box>
                    ))
                    : <Box sx={{ display: "table-cell", p: "6px 8px" }}>{t("data")}</Box>}
                </Box>
              </Box>
              <Box sx={{ display: "table-row-group" }}>
                {filteredErrorRows.map((row, index) => {
                  const cells = splitErrorData(row.errorData);
                  const badColumn = parseErrorColumn(row.errorName);
                  return (
                    <Box key={index} sx={{ display: "table-row", borderTop: 1, borderColor: "divider" }}>
                      <Box sx={{ display: "table-cell", p: "6px 8px", color: "text.disabled" }}>{index + 1}</Box>
                      <Box sx={{ display: "table-cell", p: "6px 8px", color: "error.dark" }}>{row.errorName}</Box>
                      {cells
                        ? cells.map((cell, cellIndex) => (
                          <Box
                            key={cellIndex}
                            sx={{
                              display: "table-cell", p: "6px 8px", whiteSpace: "nowrap",
                              ...(badColumn && errorHeaders[cellIndex] === badColumn
                                ? { bgcolor: "#fdecea", color: "error.dark", fontWeight: 600 }
                                : {}),
                            }}
                          >
                            {cell}
                          </Box>
                        ))
                        : <Box sx={{ display: "table-cell", p: "6px 8px", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                          {row.errorData}
                        </Box>}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box component="span" sx={{ fontSize: FS.CAPTION, color: "text.secondary", lineHeight: 1.6 }}>
            {t("MSG_ErrorRowNote")}
            {errorRows.length >= MAX_SAVED_ERROR_ROWS && ` ${t("MSG_ErrorRowsCapped", { count: MAX_SAVED_ERROR_ROWS })}`}
          </Box>
        </Box>
      </BaseConfigModal>

      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
        duration={15000}
      />
    </Box>
  )
}

export default withTranslation()(DataLoadValidation);
